import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDDPCall, useDDPSubscription } from '@zedoc/ddp-connector';
import { marked } from 'marked';
import styled from 'styled-components';
import { default as ProjectDashboardSelect } from '../../../../common/selectors/ProjectDashboard';
import { default as ProjectSelect } from '../../../../common/selectors/Project';
import { default as ProjectMilestoneSelect } from '../../../../common/selectors/ProjectMilestone';
import { default as PatientRecordSelect } from '../../../../common/selectors/PatientRecord';
import { default as QuestionnaireSelect } from '../../../../common/selectors/Questionnaire';
import { default as AnswersSheetSelect } from '../../../../common/selectors/AnswersSheet';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import Button from '../../../../components/Button';
import {
  apiZedocGetQcpData,
  apiZedocExecuteTcpFunction,
  apiZedocSearchPatientRecords,
  apiZedocProjectQuestionnaires,
} from '../../../../common/api/zedoc';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  /* min-height, not height: ProjectTabs already scrolls, and capping at the
     viewport compressed the results card until the iframe spilled out of it. */
  min-height: 100%;
`;

const FormSection = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const ResultsSection = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  /* No min-height: 0 - that would let the card shrink below its own content,
     which is what made it collapse behind the iframe. */
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #111827;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const InputField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  text-transform: capitalize;
`;

const LockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  background: #ffffff;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 12px;
  color: #111827;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }

  option {
    padding: 8px;
  }
`;

const Input = styled.input`
  width: 100%;
  background: #ffffff;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 12px;
  color: #111827;
  transition: all 0.15s ease;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover:not(:disabled) {
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }
`;

const HelpText = styled.small`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
`;

const ResultsContainer = styled.div`
  width: 100%;
  flex: 1;
  /* No border/shadow: ResultsSection is already the card, and repeating it
     drew a second box behind the result. Rounded clip only. */
  border-radius: 6px;
  overflow: hidden;

  /* The iframe is height: 100%, so this floor is what actually sizes the
     report. Replaces a fixed 500px that clipped anything longer. */
  min-height: 70vh;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block; /* avoids the inline line-box gap at exactly 100% */
  }
`;

// sql_tokens whose dropdown options are derived from patient records.
const PATIENT_RECORD_TOKENS = [
  'patient_id',
  'recipient_id',
  'participation_id',
  'activity_id',
  'answerssheet_id',
];

const DashboardFunction = ({
  projectId,
  recipientId,
  participationId,
  dashboardId,
}) => {
  const { ddpCall } = useDDPCall();

  const projectDashboard = useSelector(
    ProjectDashboardSelect.one().whereIdEquals(dashboardId),
  );

  const [functionMetadata, setFunctionMetadata] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [resultContent, setResultContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const functionId = projectDashboard?.settings?.functionId;

  // Load function metadata on mount
  useEffect(() => {
    loadMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId]);

  // Get data from Redux store
  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));

  // Patient records only back the patient, participation, activity and answer
  // sheet dropdowns. A function whose tokens need none of those would
  // otherwise pull every record in the project to the client for nothing, so
  // wait for the metadata and subscribe only when one of them is asked for.
  const needsPatientRecords = useMemo(
    () =>
      (functionMetadata?.sql_tokens || []).some((token) =>
        PATIENT_RECORD_TOKENS.includes(token),
      ),
    [functionMetadata],
  );

  // Unpaginated on purpose: these are dropdown options, so every match has to
  // be present at once rather than one page at a time.
  const { ready: patientRecordsReady } = useDDPSubscription(
    projectId && needsPatientRecords
      ? apiZedocSearchPatientRecords.withParams({
          projectId,
          controlId: `dashboard-function-${dashboardId}`,
          pageIndex: 0,
          resultsPerPage: 10000, // High limit to get all records
          filters: [],
        })
      : null,
  );

  // Subscribe to ALL questionnaires for this project
  const { ready: questionnairesReady } = useDDPSubscription(
    projectId
      ? apiZedocProjectQuestionnaires.withParams({
          projectId,
        })
      : null,
  );

  // Query PatientRecords from Redux (WITHOUT pagination filtering)
  // This is the key difference from usePagination - we get ALL records, not just one page
  const patientRecords =
    useSelector(PatientRecordSelect.all().where({ projectId })) || [];

  // Extract recipients from patient records
  const recipients = useMemo(
    () =>
      patientRecords.map((pr) => ({
        _id: pr.recipientId,
        name: { text: pr.recipientName || pr.getFullName() },
      })),
    [patientRecords],
  );

  // Extract participations from patient records
  const participations = useMemo(
    () =>
      patientRecords.map((pr) => ({
        _id: pr.participationId,
        projectId: pr.projectId,
        patientId: pr.recipientId,
        recipientId: pr.recipientId,
      })),
    [patientRecords],
  );

  // Extract activities from patient records (flattened from all records)
  const activities = useMemo(
    () =>
      patientRecords.flatMap((pr) =>
        (pr.activities || []).map((activity) => ({
          _id: activity.activityId,
          participationId: pr.participationId,
          name: activity.name || activity.milestoneName,
          projectId: pr.projectId,
        })),
      ),
    [patientRecords],
  );

  // Questionnaires - loaded via useDDPSubscription above
  const questionnaires =
    useSelector(QuestionnaireSelect.all().where({ projectId })) || [];

  // Milestones - loaded via parent apiZedocOneProject subscription
  const milestones =
    useSelector(ProjectMilestoneSelect.all().where({ projectId })) || [];

  // Answer sheets - read from Redux (loaded when viewing activities/responses)
  const answersSheets =
    useSelector(AnswersSheetSelect.all().where({ projectId })) || [];

  // Load function metadata
  const loadMetadata = async () => {
    if (!functionId) {
      notifyError()('No function ID configured for this dashboard');
      return;
    }

    setLoading(true);

    try {
      const response = await ddpCall(
        apiZedocGetQcpData.withParams({
          endpoint: `/api/tcp-functions/${functionId}/metadata`,
        }),
      );

      if (response.ok && response.metadata) {
        setFunctionMetadata(response.metadata);

        // Auto-populate inputs from project context
        const tokens = response.metadata.sql_tokens || [];
        const initial = {};

        tokens.forEach((token) => {
          if (token === 'project_id') {
            initial[token] = projectId;
          } else if (token === 'patient_id' && recipientId) {
            initial[token] = recipientId;
          } else if (token === 'recipient_id' && recipientId) {
            initial[token] = recipientId;
          } else if (token === 'participation_id' && participationId) {
            initial[token] = participationId;
          }
        });

        setInputValues(initial);
      } else {
        notifyError()('Failed to load function metadata');
      }
    } catch (error) {
      notifyError()(error.message || 'Error loading function');
    } finally {
      setLoading(false);
    }
  };

  // Execute function
  const handleExecute = async () => {
    setExecuting(true);
    setResultContent(''); // Clear previous results

    try {
      const response = await ddpCall(
        apiZedocExecuteTcpFunction.withParams({
          functionId,
          context: inputValues,
          model: 'google-gemini',
        }),
      );

      if (response.ok && response.result) {
        const { output, output_format } = response.result;

        // Create iframe-friendly content
        let iframeContent;
        if (output_format === 'html') {
          iframeContent = output;
        } else if (output_format === 'json') {
          // JSON - parse and pretty-print, then wrap in HTML with code styling
          let jsonString;
          try {
            // If output is a JSON string, parse and re-stringify for pretty printing
            const parsed =
              typeof output === 'string' ? JSON.parse(output) : output;
            jsonString = JSON.stringify(parsed, null, 2);
          } catch (e) {
            // If parsing fails, use output as-is
            jsonString =
              typeof output === 'string'
                ? output
                : JSON.stringify(output, null, 2);
          }
          iframeContent = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body {
                    margin: 0;
                    padding: 20px;
                    font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
                    background: #1e1e1e;
                    color: #d4d4d4;
                  }
                  pre {
                    margin: 0;
                    white-space: pre-wrap;
                    word-wrap: break-word;
                  }
                </style>
              </head>
              <body>
                <pre>${jsonString}</pre>
              </body>
            </html>
          `;
        } else {
          // Markdown - parse and wrap in HTML with styling
          const htmlContent = marked.parse(output);
          iframeContent = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body {
                    margin: 0;
                    padding: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                    line-height: 1.6;
                    color: #1f2937;
                    background: white;
                  }
                  h1, h2, h3, h4, h5, h6 {
                    margin-top: 24px;
                    margin-bottom: 16px;
                    font-weight: 600;
                  }
                  h1 { font-size: 2em; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
                  h2 { font-size: 1.5em; }
                  h3 { font-size: 1.25em; }
                  p { margin-bottom: 16px; }
                  pre {
                    background: #f6f8fa;
                    padding: 16px;
                    border-radius: 6px;
                    overflow-x: auto;
                    margin: 16px 0;
                  }
                  code {
                    background: #f6f8fa;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-family: 'Monaco', 'Menlo', monospace;
                    font-size: 0.9em;
                  }
                  pre code {
                    background: none;
                    padding: 0;
                  }
                  table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 16px 0;
                  }
                  th, td {
                    border: 1px solid #d1d5db;
                    padding: 8px 12px;
                    text-align: left;
                  }
                  th {
                    background: #f3f4f6;
                    font-weight: 600;
                  }
                  ul, ol {
                    margin: 16px 0;
                    padding-left: 24px;
                  }
                  li {
                    margin: 8px 0;
                  }
                  blockquote {
                    border-left: 4px solid #e5e7eb;
                    padding-left: 16px;
                    margin: 16px 0;
                    color: #6b7280;
                  }
                  a {
                    color: #3b82f6;
                    text-decoration: none;
                  }
                  a:hover {
                    text-decoration: underline;
                  }
                  strong {
                    font-weight: 600;
                  }
                </style>
              </head>
              <body>
                ${htmlContent}
              </body>
            </html>
          `;
        }

        setResultContent(iframeContent);
        notifySuccess()('Function executed successfully');
      } else {
        notifyError()('Function execution failed');
      }
    } catch (error) {
      notifyError()(error.message || 'Error executing function');
    } finally {
      setExecuting(false);
    }
  };

  // Get dropdown options based on token type with hierarchical filtering
  const getOptions = (token) => {
    // PROJECT ID - locked to current project
    if (token === 'project_id') {
      return [
        {
          value: projectId,
          label: project?.name || 'Current Project',
        },
      ];
    }

    // PATIENT/RECIPIENT ID - filter by activity/participation if selected
    if (token === 'patient_id' || token === 'recipient_id') {
      // Use recipients from DDP subscription (Redux store)
      let filtered = recipients || [];

      // If activity is selected, only show patients with that activity
      const selectedActivity = inputValues.activity_id;
      if (selectedActivity) {
        const activity = activities.find((a) => a._id === selectedActivity);
        if (activity && activity.participationId) {
          const participation = participations.find(
            (p) => p._id === activity.participationId,
          );
          if (participation) {
            const patientId =
              participation.patientId || participation.recipientId;
            if (patientId) {
              filtered = filtered.filter((r) => r._id === patientId);
            }
          }
        }
      }

      // If participation is selected (and no activity), only show patient for that participation
      const selectedParticipation = inputValues.participation_id;
      if (selectedParticipation && !selectedActivity) {
        const participation = participations.find(
          (p) => p._id === selectedParticipation,
        );

        if (participation) {
          const patientId =
            participation.patientId || participation.recipientId;
          if (patientId) {
            filtered = filtered.filter((r) => r._id === patientId);
          }
        }
      }

      return filtered.map((r) => ({
        value: r._id,
        label: r.name?.text || r._id,
      }));
    }

    // PARTICIPATION ID - filter by patient/activity if selected
    if (token === 'participation_id') {
      const selectedPatient =
        inputValues.patient_id || inputValues.recipient_id;
      const selectedActivity = inputValues.activity_id;
      let filtered = participations || [];

      // If activity is selected, only show its participation
      if (selectedActivity) {
        const activity = activities.find((a) => a._id === selectedActivity);
        if (activity && activity.participationId) {
          filtered = filtered.filter((p) => p._id === activity.participationId);
        }
      } else if (selectedPatient) {
        // If patient is selected, only show their participations
        filtered = filtered.filter(
          (p) =>
            p.patientId === selectedPatient ||
            p.recipientId === selectedPatient,
        );
      }

      return filtered.map((p) => {
        // Get patient ID from participation
        const patientId = p.patientId || p.recipientId;

        // Try to find patient in recipients array
        const patient = recipients.find((r) => r._id === patientId);

        // Get patient name - prefer name.text, fallback to just the last 8 chars of ID
        let patientName;
        if (patient?.name?.text) {
          patientName = patient.name.text;
        } else if (patientId) {
          // Show last 8 characters of patient ID if name not available
          patientName = `Patient (${patientId.slice(-8)})`;
        } else {
          patientName = 'Unknown Patient';
        }

        // Get project name - should always be available
        const projectName = project?.name || project?._id || 'Project';

        return {
          value: p._id,
          label: `${patientName} in ${projectName} - ${p._id}`,
        };
      });
    }

    // ACTIVITY ID - filter by patient/participation if selected
    if (token === 'activity_id') {
      const selectedPatient =
        inputValues.patient_id || inputValues.recipient_id;
      const selectedParticipation = inputValues.participation_id;
      let filtered = activities; // Already filtered by project via participations

      if (selectedParticipation) {
        // If participation is selected, only show its activities
        filtered = filtered.filter(
          (a) => a.participationId === selectedParticipation,
        );
      } else if (selectedPatient) {
        // If patient is selected, only show activities for their participations
        const patientParticipations = participations.filter(
          (p) =>
            p.patientId === selectedPatient ||
            p.recipientId === selectedPatient,
        );
        const participationIds = patientParticipations.map((p) => p._id);
        filtered = filtered.filter((a) =>
          participationIds.includes(a.participationId),
        );
      }

      return filtered.map((a) => ({
        value: a._id,
        label: a.name || a._id,
      }));
    }

    // QUESTIONNAIRE ID - show all questionnaires in project
    if (token === 'questionnaire_id') {
      return questionnaires.map((q) => ({
        value: q._id,
        label: q.name || q._id,
      }));
    }

    // MILESTONE ID - show all milestones in project
    if (token === 'milestone_id') {
      return (milestones || []).map((m) => ({
        value: m._id,
        label: m.name || m._id,
      }));
    }

    // ANSWERS SHEET ID - filter by activity/participation/patient if selected
    if (token === 'answerssheet_id') {
      const selectedPatient =
        inputValues.patient_id || inputValues.recipient_id;
      const selectedParticipation = inputValues.participation_id;
      const selectedActivity = inputValues.activity_id;
      let filtered = answersSheets || [];

      if (selectedActivity) {
        // If activity is selected, only show its answer sheets
        filtered = filtered.filter((a) => a.activityId === selectedActivity);
      } else if (selectedParticipation) {
        // If participation is selected, only show its answer sheets
        filtered = filtered.filter(
          (a) => a.participationId === selectedParticipation,
        );
      } else if (selectedPatient) {
        // If patient is selected, only show their answer sheets
        filtered = filtered.filter(
          (a) =>
            a.patientId === selectedPatient ||
            a.recipientId === selectedPatient,
        );
      }

      return filtered.map((answerSheet) => {
        // Show activity name and patient context with full answer sheet ID
        const activity = activities.find(
          (a) => a._id === answerSheet.activityId,
        );
        const activityName = activity?.name || 'Activity';

        const patientId = answerSheet.patientId || answerSheet.recipientId;
        const patient = recipients.find((r) => r._id === patientId);
        const patientName = patient?.name?.text || 'Patient';

        return {
          value: answerSheet._id,
          label: `${patientName} - ${activityName} - ${answerSheet._id}`,
        };
      });
    }

    return [];
  };

  const renderInputField = (token) => {
    const options = getOptions(token);
    const isAutoSelected = token === 'project_id';
    const isDateField = token.includes('date') || token.includes('Date');
    // All ID fields should be dropdowns (no manual input allowed)
    const isIdField = token.endsWith('_id') || token.endsWith('_ID');
    const isDropdown = isIdField || options.length > 0;

    // Determine filtering status for help text
    const getFilterStatus = () => {
      if (token === 'project_id') {
        return 'Locked to current project';
      }

      if (token === 'patient_id' || token === 'recipient_id') {
        if (inputValues.activity_id) {
          return 'Filtered by selected activity';
        }
        if (inputValues.participation_id) {
          return 'Filtered by selected participation';
        }
        return `Showing ${options.length} patient(s) in this project`;
      }

      if (token === 'participation_id') {
        if (inputValues.activity_id) {
          return 'Filtered by selected activity';
        }
        if (inputValues.patient_id || inputValues.recipient_id) {
          return 'Filtered by selected patient';
        }
        return `Showing ${options.length} participation(s)`;
      }

      if (token === 'activity_id') {
        if (inputValues.participation_id) {
          return 'Filtered by selected participation';
        }
        if (inputValues.patient_id || inputValues.recipient_id) {
          return 'Filtered by selected patient';
        }
        return `Showing ${options.length} activit${
          options.length === 1 ? 'y' : 'ies'
        }`;
      }

      if (token === 'questionnaire_id') {
        return `Showing ${options.length} questionnaire(s) in this project`;
      }

      if (token === 'milestone_id') {
        return `Showing ${options.length} milestone(s) in this project`;
      }

      if (token === 'answerssheet_id') {
        if (inputValues.activity_id) {
          return 'Filtered by selected activity';
        }
        if (inputValues.participation_id) {
          return 'Filtered by selected participation';
        }
        if (inputValues.patient_id || inputValues.recipient_id) {
          return 'Filtered by selected patient';
        }
        return `Showing ${options.length} answer sheet(s)`;
      }

      return null;
    };

    const filterStatus = getFilterStatus();

    return (
      <InputField key={token}>
        <Label>
          {token.replace(/_/g, ' ')}
          {isAutoSelected && <LockBadge>Locked</LockBadge>}
        </Label>

        {isDropdown && (
          <Select
            value={inputValues[token] || ''}
            onChange={(e) => {
              const newValues = { ...inputValues, [token]: e.target.value };

              // Smart hierarchical clearing - clear dependent fields
              if (token === 'project_id') {
                // Clear everything if project changes (shouldn't happen)
                delete newValues.patient_id;
                delete newValues.recipient_id;
                delete newValues.participation_id;
                delete newValues.activity_id;
                delete newValues.answerssheet_id;
              } else if (token === 'patient_id' || token === 'recipient_id') {
                // Clear participation and activity when patient changes
                delete newValues.participation_id;
                delete newValues.activity_id;
                delete newValues.answerssheet_id;
              } else if (token === 'participation_id') {
                // Clear activity when participation changes
                delete newValues.activity_id;
                delete newValues.answerssheet_id;
              } else if (token === 'activity_id') {
                // When activity changes, update participation and patient if needed
                const activity = activities.find(
                  (a) => a._id === e.target.value,
                );
                if (activity && activity.participationId) {
                  // Auto-set participation from activity
                  newValues.participation_id = activity.participationId;

                  // Auto-set patient from participation
                  const participation = participations.find(
                    (p) => p._id === activity.participationId,
                  );
                  if (participation) {
                    const patientId =
                      participation.patientId || participation.recipientId;
                    if (patientId) {
                      if (functionMetadata.sql_tokens?.includes('patient_id')) {
                        newValues.patient_id = patientId;
                      } else if (
                        functionMetadata.sql_tokens?.includes('recipient_id')
                      ) {
                        newValues.recipient_id = patientId;
                      }
                    }
                  }
                }
                delete newValues.answerssheet_id;
              } else if (token === 'answerssheet_id') {
                // When answer sheet changes, auto-set activity/participation/patient if possible
                const answerSheet = (answersSheets || []).find(
                  (a) => a._id === e.target.value,
                );
                if (answerSheet) {
                  if (answerSheet.activityId) {
                    newValues.activity_id = answerSheet.activityId;
                    const activity = activities.find(
                      (a) => a._id === answerSheet.activityId,
                    );
                    if (activity && activity.participationId) {
                      newValues.participation_id = activity.participationId;
                      const participation = participations.find(
                        (p) => p._id === activity.participationId,
                      );
                      if (participation) {
                        const patientId =
                          participation.patientId || participation.recipientId;
                        if (patientId) {
                          if (
                            functionMetadata.sql_tokens?.includes('patient_id')
                          ) {
                            newValues.patient_id = patientId;
                          } else if (
                            functionMetadata.sql_tokens?.includes(
                              'recipient_id',
                            )
                          ) {
                            newValues.recipient_id = patientId;
                          }
                        }
                      }
                    }
                  } else if (answerSheet.participationId) {
                    newValues.participation_id = answerSheet.participationId;
                  }
                }
              }

              setInputValues(newValues);
            }}
            disabled={isAutoSelected || loading}
          >
            <option value="">
              {options.length === 0
                ? `No ${token.replace(/_/g, ' ')} available`
                : `Select ${token.replace(/_/g, ' ')}...`}
            </option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        )}

        {!isDropdown && isDateField && (
          <Input
            type="date"
            value={inputValues[token] || ''}
            onChange={(e) =>
              setInputValues({ ...inputValues, [token]: e.target.value })
            }
            disabled={loading}
          />
        )}

        {!isDropdown && !isDateField && (
          <Input
            type="text"
            value={inputValues[token] || ''}
            onChange={(e) =>
              setInputValues({ ...inputValues, [token]: e.target.value })
            }
            placeholder={`Enter ${token.replace(/_/g, ' ')}...`}
            disabled={loading}
          />
        )}

        {filterStatus && <HelpText>{filterStatus}</HelpText>}
      </InputField>
    );
  };

  if (loading && !functionMetadata) {
    return (
      <Container>
        <FormSection>
          <div className="text-center py-10 text-secondary">
            Loading function...
          </div>
        </FormSection>
      </Container>
    );
  }

  if (!functionMetadata) {
    return (
      <Container>
        <FormSection>
          <div className="text-center py-10 text-secondary">
            Function not found
          </div>
        </FormSection>
      </Container>
    );
  }

  return (
    <Container>
      <FormSection>
        <SectionTitle>{functionMetadata.name}</SectionTitle>
        {functionMetadata.description && (
          <p
            style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '20px',
              lineHeight: '1.5',
            }}
          >
            {functionMetadata.description}
          </p>
        )}

        <InputGrid>
          {functionMetadata.sql_tokens?.map((token) => renderInputField(token))}
        </InputGrid>

        <ButtonContainer>
          <Button onClick={handleExecute} disabled={executing} type="primary">
            {executing ? 'Executing...' : 'Execute Function'}
          </Button>
        </ButtonContainer>
      </FormSection>

      {resultContent && (
        <ResultsSection>
          <SectionTitle>Results</SectionTitle>
          <ResultsContainer>
            {/* SECURITY: no allow-same-origin. srcDoc is unsanitized model
                output, and allow-scripts + allow-same-origin together are a
                sandbox escape (scripts would run as the ZeDOC origin). Do not
                re-add it without sanitizing resultContent first. */}
            <iframe
              srcDoc={resultContent}
              title="Function Results"
              sandbox="allow-scripts allow-popups"
            />
          </ResultsContainer>
        </ResultsSection>
      )}
    </Container>
  );
};

DashboardFunction.propTypes = {
  projectId: PropTypes.string.isRequired,
  recipientId: PropTypes.string,
  participationId: PropTypes.string,
  dashboardId: PropTypes.string.isRequired,
};

DashboardFunction.defaultProps = {
  recipientId: null,
  participationId: null,
};

// Loads ALL data for dropdowns:
// - PatientRecords (via usePagination) → recipients, participations, activities (10,000 limit)
// - Questionnaires (via useDDPSubscription) → questionnaires
// - Milestones (via parent apiZedocOneProject) → milestones
// - AnswersSheets (from Redux cache) → answer sheets (loaded when viewing responses)
// All data loaded with high limits to ensure complete selection lists
export default DashboardFunction;
